import type { LucideIcon } from 'lucide-react';
import { create } from 'zustand';
import { useUIStore } from './uiStore';

/**
 * The right-hand side dock: one column for every page. A rail of icons — the AI assistant on
 * top, then the tools of the page that is open — and, when one is chosen, its panel beside it.
 * A page registers its tools (useDockTools) and renders their panels itself through a portal
 * into `panelEl`, so the panel keeps the page's state.
 *
 * Open / closed is the persisted `aiPanelCollapsed` of uiStore (the dock replaced the AI panel);
 * which panel shows (`view`) is per visit: 'ai' or a tool id.
 */
export interface DockTool {
  id: string;
  label: string;
  icon: LucideIcon;
  /** a small number on the icon (e.g. active filters) */
  badge?: number;
}

export const AI_VIEW = 'ai';
/** the dock's own panels, on every page (SideDock renders them) */
export const BUILTIN_VIEWS = ['compare', 'follow'];

interface DockState {
  tools: DockTool[];
  /** who registered `tools` — a page leaving clears only its own */
  owner: string | null;
  view: string;
  panelEl: HTMLElement | null;
  setTools: (owner: string, tools: DockTool[]) => void;
  clearTools: (owner: string) => void;
  setPanelEl: (el: HTMLElement | null) => void;
  /** open the dock on this panel */
  show: (view: string) => void;
  /** close the dock if this panel is the one showing */
  hide: (view: string) => void;
  /** the rail's click: open it, or close it when it is already open */
  toggle: (view: string) => void;
}

const isOpen = () => !useUIStore.getState().aiPanelCollapsed;
const setOpen = (open: boolean) => useUIStore.setState({ aiPanelCollapsed: !open });

export const useSideDock = create<DockState>()((set, get) => ({
  tools: [],
  owner: null,
  view: AI_VIEW,
  panelEl: null,
  setTools: (owner, tools) => set({ owner, tools }),
  clearTools: (owner) => {
    if (get().owner !== owner) return;
    const gone = get().view !== AI_VIEW && !BUILTIN_VIEWS.includes(get().view);
    set({ owner: null, tools: [], ...(gone ? { view: AI_VIEW } : {}) });
  },
  setPanelEl: (panelEl) => set({ panelEl }),
  show: (view) => { set({ view }); setOpen(true); },
  hide: (view) => { if (get().view === view && isOpen()) setOpen(false); },
  toggle: (view) => {
    if (isOpen() && get().view === view) setOpen(false);
    else { set({ view }); setOpen(true); }
  },
}));
